import { configureSidekickClient } from '@brightdrop/ai-sidekick/api';
import {
  configureEntitlementsClient,
  configurePublicApi,
} from '@gm-commercial/entitlements-client';

import { configureStore, type Reducer } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/dist/query';
import {
  type TypedUseSelectorHook,
  useDispatch,
  useSelector,
} from 'react-redux';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  persistStore,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';

import { VehicleOrdersActionType } from '~/features/orders/state/vehicleOrdersSlice.actions';

import { clearPersistedActingContext } from '~/store/profile/profileSlice';
import { API_CLIENTS } from './apiClients';
import { staticReducers } from './coreReducer';
import { createReducerManager } from './reducerManager';
import type { RootState } from './rootReducer';

const reducerManager = createReducerManager(staticReducers);

export const store = configureStore({
  reducer: reducerManager.getReducer(),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          `${VehicleOrdersActionType.GET_ORDER_LIST_EXPORT}/fulfilled`, //returns blob
          'tripHistory/selectDate', // Date object is converted to string in reducer
        ],
      },
    }).concat(API_CLIENTS.map((client) => client.middleware)),
});
export const persistor = persistStore(store);

/**
 * Removes persisted view-as / impersonation data from Redux and localStorage.
 * Call before logout redirect and session timeout sign-out.
 */
export const clearPersistedProfileSessionData = async (): Promise<void> => {
  store.dispatch(clearPersistedActingContext());
  await persistor.flush();
};

/**
 * Registers a feature slice reducer at runtime and swaps it into the live store.
 *
 * Call this as part of lazily loading the route/feature that owns the slice so
 * the slice's module graph stays out of the initial (pre-auth) bundle. Safe to
 * call repeatedly — already-mounted reducers are ignored.
 *
 * @param key - The state key the reducer manages (e.g. `'geofence'`).
 * @param reducer - The slice reducer to mount.
 */
export const injectReducer = (key: string, reducer: Reducer): void => {
  reducerManager.add(key, reducer);
  store.replaceReducer(reducerManager.getReducer());
};
configureEntitlementsClient({
  apiBaseUrl: globalThis.appConfig.apiBaseUrl,
});

configurePublicApi({
  apiBaseUrl: globalThis.appConfig.apiBaseUrl,
});

// The Sidekick client appends its own `/sidekick` segment, so it takes
// the platform root rather than a service URL.
configureSidekickClient({
  platformApiBaseUrl: globalThis.appConfig.apiBaseUrl,
});

setupListeners(store.dispatch);

export type AppStore = typeof store;
export type AppDispatch = AppStore['dispatch'];

export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelect: TypedUseSelectorHook<RootState> = useSelector;
